import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Table } from 'reactstrap';
import { TenantPicker } from '../../components/selectors/TenantPicker';
import { WarehousePicker } from '../../components/selectors/WarehousePicker';
import { PageHeading } from '../../layouts/PageHeading';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { useListItemSchemas } from '../../api/itemSchemas';

export function ItemSchemasList() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const [tenantId, setTenantId] = useState<number | null>(search.get('tenant') ? Number(search.get('tenant')) : null);
  const [warehouseId, setWarehouseId] = useState<number | null>(search.get('warehouse') ? Number(search.get('warehouse')) : null);

  const { data, isLoading } = useListItemSchemas(warehouseId, token);
  const items = data?.items ?? [];

  const updateUrl = (t: number | null, w: number | null) => {
    const p = new URLSearchParams();
    if (t !== null) p.set('tenant', String(t));
    if (w !== null) p.set('warehouse', String(w));
    navigate({ search: p.toString() }, { replace: true });
  };

  return (
    <div>
      <PageHeading
        scope="Item schemas"
        meta={<span>Schemi di attributi per warehouse, versionati e immutabili</span>}
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={tenantId === null || warehouseId === null}
            onClick={() => navigate(`/admin/item-schemas/new?tenant=${tenantId}&warehouse=${warehouseId}`)}
          >
            + Nuova versione
          </button>
        }
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <TenantPicker
          value={tenantId}
          onChange={(t) => { setTenantId(t); setWarehouseId(null); updateUrl(t, null); }}
        />
        <WarehousePicker
          tenantId={tenantId}
          value={warehouseId}
          onChange={(w) => { setWarehouseId(w); updateUrl(tenantId, w); }}
        />
      </div>

      {warehouseId === null ? (
        <EmptyState
          icon={<i className="bi bi-funnel" />}
          title="Seleziona tenant e warehouse"
          body="La lista degli schemi è per warehouse — seleziona prima i due selettori in alto."
        />
      ) : isLoading ? (
        <div style={{ padding: 24, color: 'var(--vp-text-3)' }}>Caricamento…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<i className="bi bi-diagram-3" />}
          title="Nessuno schema per questo warehouse"
          body="Crea la prima versione per iniziare."
          cta={{
            label: '+ Nuova versione',
            onClick: () => navigate(`/admin/item-schemas/new?tenant=${tenantId}&warehouse=${warehouseId}`),
          }}
        />
      ) : (
        <div className="table-responsive">
        <Table className="table-hover align-middle table-striped mb-0">
          <thead>
            <tr>
              <th>Version</th>
              <th>Stato</th>
              <th>Attributi</th>
              <th>Voice attrs</th>
              <th>Created at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => {
              const total = s.schema.attributes.length;
              const voice = s.schema.attributes.filter((a) => a.voice_role !== 'none').length;
              return (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/item-schemas/${s.id}?tenant=${tenantId}&warehouse=${warehouseId}`)}>
                  <td className="font-monospace">v{s.version}</td>
                  <td>{s.is_active ? <StatusBadge tone="success">attiva</StatusBadge> : <StatusBadge>prev</StatusBadge>}</td>
                  <td>{total}</td>
                  <td>{voice}/{total}</td>
                  <td className="font-monospace">{s.created_at.slice(0, 10)}</td>
                  <td>→</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </div>
      )}
    </div>
  );
}

export default ItemSchemasList;
